import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { Spiner } from "../../components/spinner/Spiner";
import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../../firebase";
import Button from "../../components/button/Button";
import { useNavigate, useParams } from "react-router-dom";
import { EventParticipationSearch } from "./EventParticipationSearch";
import { Company } from "../../model/Companies";
import companiesQuery from "../../firestore/Companies.query";
import messagesQuery from "../../firestore/Messages.query";
import { convertDateTimeToString } from "../../utility/ConvertUtily";
import { EventParticipationExportModal } from "./EventParticipationExportModal";
import { Progress } from "../../components/progress/Progress";
import { ConstMessage } from "../../config/const";
import { EventParticipationHistory, EventParticipationHistorySearchParam, RdbEventParticipationHistoryMapping } from "../../model/EventParticipationHistory";
import { requestGetRdbEventParticipationHistory } from "../../api/Messages.api";

type Props = {
  targetUserId?: string | undefined;
};

export const EventParticipation = ({
  targetUserId = undefined
}:Props) => {

  const navigation = useNavigate();
  const { messageId } = useParams<{ messageId: string }>();

  const href = useRef<string>(window.location.href);
  const targetMessageId = useRef<string>(messageId ?? "");

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;

  const [isLoad, setIsLoad] = useState(true);
  const [isSearchingLoad, setIsSearchingLoad] = useState(false);
  const [columns, setColumns] = useState<GridCol[]>([]);
  const [rowList, setRowList] = useState<GridRow[]>([]);
  const [company, setCompany] = useState<Company>();
  const [nowPage, setNowPage] = useState<number>(1);
  const [selectNumber, setSelectNumber] = useState<number>(50);
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [searchCondition, setSearchCondition] = useState<EventParticipationHistorySearchParam>({
      messageId: messageId ?? "",
      replyListValue: [],
      attendanceListValue: [],
      publishTarget: "",
      searchGroups: [],
      userId: targetUserId ?? "",
      lastName: "",
      title: "",
      eventVenue: "",
      sendDateStart: "",
      sendDateEnd: "",
      eventAtStart: "",
      eventAtEnd: "",
      eventType: "",
      stringCombinationSearchFlg: false,
      timestampCombinationSearchFlg: false
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [targetMessage, setTargetMessage] = useState<any>(null);

  // 表示件数
  const selectNumberList = [
    { id: 50, name: "50" },
    { id: 100, name: "100" },
    { id: 200, name: "200" },
  ];

  useEffect(() => {
    (async () => {
      if (targetMessageId.current !== "") {
        // messageIdのチェック
        try {
          const targetMessage = await messagesQuery.getMessage(targetMessageId.current);
          setTargetMessage(targetMessage);
          if (!targetMessage.municipalityConfirmedFlg) navigation(`/eventParticipation`);
        } catch (e: any) {
          navigation(`/eventParticipation`);
        }
      }

      // 団体を取得
      const company = await companiesQuery.getCompany(currentUser?.tenantId!);

      // イベント参加履歴一覧カラムを設定
      const columns: GridCol[] = [];
      if (targetMessageId.current === "") {
        // 遷移元がお知らせ詳細画面以外の場合
        columns.push({field: "title", headerName: "タイトル", style: "w-2/12", sort: false});
      }
      columns.push(
        {field: "eventVenue", headerName: "会場", style: "w-1/12", sort: false},
        {field: "sendDate", headerName: "配信日時", style: "w-1/12", sort: false},
        {field: "eventAt", headerName: "イベント開催日時", style: "w-1/12", sort: false}
      );
      if (targetUserId === undefined) {
        // 遷移元が利用者詳細画面以外の場合
        columns.push(
          {field: "displayId", headerName: "利用者番号", style: "w-1/12", sort: false},
          {field: "lastName", headerName: "名前", style: "w-1/12", sort: false}
        );
      }
      columns.push(
        {field: "replyButtonName", headerName: "利用者の回答", style: "w-1/12", sort: false},
        {field: "attendanceButtonName", headerName: "出欠結果", style: "w-1/12", sort: false},
        {field: "menu", headerName: "", style: "w-1/12", sort: false}
      );

      if (targetUserId !== undefined || targetMessageId.current !== "") {
        // 遷移元がお知らせ詳細画面または利用者詳細画面の場合
        // イベント参加履歴の取得処理を発火
        setSearchCondition({...searchCondition});
      }

      setColumns(columns);
      setCompany(company);
      setIsLoad(false);
    })();
  },[]);

  // イベント参加履歴一覧を取得する共通処理
  useEffect(() => {
    const f = async () => {
      const result = await requestGetRdbEventParticipationHistory(currentUser?.tenantId!, company?.displayId!, selectNumber, nowPage, searchCondition);
      if (result.success) {
        console.log('result:', result)
        rowListDataSet(result.data.rows.map((item: any) => RdbEventParticipationHistoryMapping(item)));
        setTotalCount(result.data.count);
      } else {
        console.error("イベント参加履歴取得失敗:", result);
      }
      setIsSearchingLoad(false)
    };
    if (checkSearchParam()) f();
  }, [nowPage, selectNumber, searchCondition]);

  /** 検索項目の入力チェック */
  function checkSearchParam () {
    return !(
      searchCondition.messageId === "" &&
      searchCondition.replyListValue.filter((reply) => reply.value === true).length === 0 &&
      searchCondition.attendanceListValue.filter((attendance) => attendance.value === true).length === 0 &&
      searchCondition.publishTarget === "" &&
      searchCondition.searchGroups.filter((group) => group.value === true).length === 0 &&
      searchCondition.userId === "" &&
      searchCondition.lastName === "" &&
      searchCondition.title === "" &&
      searchCondition.eventVenue === "" &&
      searchCondition.sendDateStart === "" &&
      searchCondition.sendDateEnd === "" &&
      searchCondition.eventAtStart === "" &&
      searchCondition.eventAtEnd === "" &&
      searchCondition.eventType === ""
    );
  }

  async function rowListDataSet(historyList: EventParticipationHistory[]) {
    const rowData: GridRow[] = [];

    await Promise.all(historyList.map(async (history: EventParticipationHistory) => {
      const destinatione = href.current.includes('eventParticipation') ? "eventParticipation" : `userList_${history.userId}`;
      rowData.push({
        rowData: {
          displayId: history.userDisplayId,
          lastName: history.lastName,
          title: history.title,
          publishTarget: history.publishTarget,
          publishTargetList: history.publishTargetList,
          eventVenue: history.eventVenue,
          sendDate: convertDateTimeToString(history.sendDate),
          eventAt: convertDateTimeToString(history.eventAt),
          eventType: history.eventType,
          replyButtonName: history.replyButtonName === "" ? "未回答" : history.replyButtonName, 
          attendanceButtonName: history.attendanceButtonName === "" ? "未回答" : history.attendanceButtonName,
          menu: (
            <Button
              onClick={() => navigation(`/message/${history.messageId}/info?transitionSource=${destinatione}`)}
              className={"sm:px-3"}
              children="詳細" />
          )
        },
        style: "truncate max-w-0 sm:whitespace-normal",
      });
    }));
    setRowList(rowData);
  }

  // 検索ボタンが押されたときの処理
  const handleSearch = (condition: any) => {
    setIsSearchingLoad(true);
    setSearchCondition(condition);
    setNowPage(1); // 検索時は常に1ページ目に戻す
  };

  // ページング処理
  const pagingFunc = useCallback((page: number) => {
    setFirstPageFlg(false);
    setNowPage(page);
  }, []);

  return (
    <div className="h-full">
      <div className="space-x-4 p-1 px-6 pb-2 flex items-center justify-between">
        {targetUserId === undefined && (
          <h2 className="pt-5 min-w-[120px] text-xl font-semibold text-gray-900">
            イベント参加履歴一覧
          </h2>
        )}
        {messageId && (
          <div className="flex space-x-2">
            <Button
              size="sm"
              onClick={() => navigation(`/message/${messageId}/info?scrollTo=deliveryResult`)}
            >
              配信先一覧
            </Button>
            <Button
              size="sm"
              onClick={() => navigation(`/message/${messageId}/reply`)}
            >
              返信一覧
            </Button>
            {targetMessage?.eventFlg && (
              <Button
                size="sm"
                onClick={() => navigation(`/message/${messageId}/attendance`)}
              >
                出欠一覧
              </Button>
            )}
            {targetMessage?.eventFlg && (
              <Button
                size="sm"
                disabled={!(
                  targetMessage.questionnaireFlg &&
                  targetMessage.eventEndAt &&
                  new Date() > new Date(targetMessage.eventEndAt)
                )}
                className={
                  !(targetMessage.questionnaireFlg &&
                  targetMessage.eventEndAt &&
                  new Date() > new Date(targetMessage.eventEndAt))
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
                onClick={() => navigation(`/message/${messageId}/questionnaireResults`)}
              >
                アンケート結果
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="pl-7 pt-3 pb-4">
          <Button
            size={"sm"}
            className={"w-[132px]"}
          onClick={() => {setIsDialogOpen(!isDialogOpen)}}
            children={"履歴出力"}
          />
      </div>
      <Progress
        subText={ConstMessage.PROGRESS_SUB_TEXT}
        open={loadProgress >= 0 && loadProgress < 100}
        progressNum={loadProgress}
        color={"indigo"}
      />
      {/* 履歴出力確認モーダル */}
      <EventParticipationExportModal
        children={undefined}
        open={isDialogOpen}
        toggle={() => {setIsDialogOpen(!isDialogOpen)}}
        closeOnClickOutside={false}
        company={company}
        targetUserId={targetUserId}
        searchParams={searchCondition}
        stateSetLoadProgress={setLoadProgress}
        checkSearchParam={checkSearchParam()}
      />
      {isLoad ? <Spiner /> : (
        <>
          <div className={'px-6 pb-3'}>
            <EventParticipationSearch
              company={company}
              targetUserId={targetUserId}
              targetMessageId={targetMessageId.current}
              handleSearch={handleSearch}
             />
          </div>
          <div className={'px-6 pb-4'}>
            {isSearchingLoad ? <Spiner /> : (
              <TableList
                header={true}
                gridCol={columns}
                gridRow={rowList}
                limit={selectNumber}
                selectNumberList={selectNumberList}
                setSelectNumber={setSelectNumber}
                gridRowLength={totalCount}
                firstPageFlg={firstPageFlg}
                setFirstPageFlg={setFirstPageFlg}
                pagingFunc={pagingFunc}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
